import './index.scss';

import React, { useEffect, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import axios from '../../admin-axios';
import useActions from '../../hooks/useActions';
import { detectError } from '../../utils';
import CreateCINBoxRow from '../CreateCINBoxRow';
import { PromptModal, SuccessModal } from '../Modals';

const CreateCIN = () => {
  const { setBackLink } = useActions();
  const { id, first, last } = useParams();
  const [gasQty, setGasQty] = useState('');
  const [addGasQtyLoading, setAddGasQtyLoading] = useState(false);
  const [cinList, setCinList] = useState([]);
  const [cinListLoading, setCinListLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showCreateTransaction, setCreateTransactionShow] = useState(false);
  const [createTransactionLoading, setCreateTransactionLoading] = useState(false);

  const handleCreateTransactionClose = () => setCreateTransactionShow(false);
  const handleCreateTransactionShow = () => setCreateTransactionShow(true);

  const {
    user: { stationId, id: user_id }
  } = useSelector((state) => state.auth);

  const { getUnApprovedTransactions } = useActions();

  const addGasQuantityHandler = async () => {
    if (gasQty === '') {
      toast.error('Please add a gas quantity');
      return;
    }

    if (Number.isNaN(+gasQty)) {
      toast.error('Please add a number value');
      return;
    }

    try {
      setAddGasQtyLoading(true);
      await axios.post(`/cylinder/cin/add`, {
        station_id: stationId,
        customer_id: id,
        gas_quantity: gasQty,
        created_by: user_id
      });
      setAddGasQtyLoading(false);
      setGasQty('');
      toast.success('CIN Created');

      //  fetch CINs
      setCinListLoading(true);
      const response = await axios.get(`/cylinder/cin/view/${id}`);
      setCinListLoading(false);
      setCinList(response.data.data);
    } catch (e) {
      setAddGasQtyLoading(false);
      setCinListLoading(false);
      toast.error(`Error Creating CIN ${e.message}`);
    }
  };

  const createTransactionHandler = async () => {
    try {
      setCreateTransactionLoading(true);
      await axios.get(`/transaction/create/${id}/${user_id}`);
      setCreateTransactionLoading(false);
      handleCreateTransactionClose();
      setShowSuccess(true);
      getUnApprovedTransactions(stationId, 1);
    } catch (e) {
      setCreateTransactionLoading(false);
      toast.error(`Error creating transaction: ${detectError(e)}`);
    }
  };

  useEffect(() => {
    const fetchCins = async () => {
      try {
        setCinListLoading(true);
        const response = await axios.get(`/cylinder/cin/view/${id}`);
        setCinList(response.data.data);
        setCinListLoading(false);
      } catch (e) {
        setCinListLoading(false);
        toast.error(`Error Loading CINs ${e.message}`);
      }
    };

    fetchCins();
  }, [id]);

  useEffect(() => {
    return () => {
      setBackLink('');
    };
  }, []);

  return (
    <>
      <div className="w-100 h-100 create_cin">
        <h2 className="mb-4">
          {first} {last}
        </h2>
        <div className="row g-4">
          <div className="col-lg-6">
            <div>
              <input
                className="form-control form-control-lg mb-3"
                type="text"
                placeholder="Gas Quantity"
                value={gasQty}
                onChange={(e) => setGasQty(e.target.value)}
                aria-label=".form-control-lg example"
              />
              {addGasQtyLoading ? (
                <div className="text-center d-flex justify-content-center w-100 h-100">
                  <Spinner animation="border" variant="dark" />
                </div>
              ) : (
                <button
                  onClick={addGasQuantityHandler}
                  type="button"
                  className="btn create_cin_button btn-lg w-100 p-2">
                  Add
                </button>
              )}
            </div>
          </div>
          <div className="col-lg-6">
            <div className="create_cin_list p-3">
              <h5 className="text-center mb-3">Cylinder Identification Numbers</h5>
              <div className="create_cin_list_box mb-5">
                {cinListLoading ? (
                  <div className="text-center d-flex justify-content-center w-100 h-100">
                    <Spinner animation="border" variant="dark" />
                  </div>
                ) : (
                  <>
                    {!cinList && <p className="text-center">Add CIN to cart</p>}
                    {cinList && cinList.length === 0 && (
                      <p className="text-center">Add CIN to cart</p>
                    )}
                    {cinList &&
                      cinList.length > 0 &&
                      cinList.map((cin) => {
                        return (
                          <CreateCINBoxRow
                            key={cin.cin}
                            cin={cin}
                            customer_id={id}
                            setCinList={setCinList}
                          />
                        );
                      })}
                  </>
                )}
              </div>
              <button
                disabled={!cinList}
                onClick={() => handleCreateTransactionShow()}
                type="button"
                className="btn create_cin_create_transaction_button btn-lg w-100 p-2">
                Create Transaction
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="create_cin_modals">
        <PromptModal
          title="Create Transaction"
          message="Are you sure you want to create a transaction for this customer"
          showPrompt={showCreateTransaction}
          handlePromptClose={handleCreateTransactionClose}
          promptActionHandler={createTransactionHandler}
          loading={createTransactionLoading}
        />
        <SuccessModal
          message="You've successfully created a transaction for this customer"
          showSuccess={showSuccess}
          setShowSuccess={setShowSuccess}
          redirect="/createcin"
        />
      </div>
    </>
  );
};

export default CreateCIN;
